import {
  COMPANY_REGISTRY_FIELDS,
  type CompanyLookupOutput,
  type CompanyRegistryField,
  formatCompanyAddress,
} from '@fos/shared/contracts/company-registry';
import type { ClientCompany, ClientDto } from '@fos/shared/contracts/crm';
import { COMPANY_REGISTRY_FIELD_LABELS, COMPANY_VAT_STATUS_LABELS } from '@fos/shared/labels';
import { formatNip, normalizeNip } from '@fos/shared/nip';
import { useEffect, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Spinner } from '../../../ui/Spinner';
import { toast } from '../../../ui/Toast';
import { registrySourceLabel } from './CompanyLookup';

/** Wartość pola klienta do porównania (tekst; puste = „—”). */
function fieldText(field: CompanyRegistryField, company: Partial<ClientCompany> | null | undefined): string {
  if (!company) return '';
  if (field === 'address') return formatCompanyAddress(company.address);
  if (field === 'vatStatus') return company.vatStatus ? COMPANY_VAT_STATUS_LABELS[company.vatStatus] : '';
  return (company[field] as string | undefined) ?? '';
}

/**
 * Wartość z rejestru: status VAT „nieznany” (REGON nie podaje statusu VAT) = brak danych — wiersz nie jest zaznaczany
 * ani wybieralny, więc znany status klienta (np. z Białej listy) nie przepada.
 */
function registryText(field: CompanyRegistryField, company: Partial<ClientCompany> | null | undefined): string {
  if (field === 'vatStatus' && company?.vatStatus === 'unknown') return '';
  return fieldText(field, company);
}

/**
 * „Odśwież dane z rejestru” (karta klienta → „…”; widok bez projektu w Figmie): dane z rejestru dla NIP klienta,
 * porównanie pole po polu (teraz → w rejestrze), zaznaczone tylko pola, które się różnią. Zapis = `clients.applyRegistry`
 * (wartości z pamięci podręcznej serwera, nie z przeglądarki). Nazwa wyświetlana klienta się nie zmienia.
 */
export function RegistryRefreshDialog({
  open,
  onOpenChange,
  client,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: ClientDto;
}) {
  const utils = trpc.useUtils();
  const lookup = trpc.clients.lookupNip.useMutation();
  const apply = trpc.clients.applyRegistry.useMutation();
  const [result, setResult] = useState<CompanyLookupOutput | null>(null);
  const [selected, setSelected] = useState<Set<CompanyRegistryField>>(new Set());
  const nip = normalizeNip(client.company.nip);

  // biome-ignore lint/correctness/useExhaustiveDependencies: wyszukanie tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setSelected(new Set());
    apply.reset();
    lookup.reset();
    if (!nip) return;
    lookup.mutate(
      { nip },
      {
        onSuccess: (r) => {
          setResult(r);
          const differing = COMPANY_REGISTRY_FIELDS.filter(
            (f) =>
              r.company && registryText(f, r.company) && registryText(f, r.company) !== fieldText(f, client.company),
          );
          setSelected(new Set(differing));
        },
      },
    );
  }, [open]);

  async function save() {
    if (!result?.found || selected.size === 0) return;
    try {
      await apply.mutateAsync({ clientId: client.id, version: client.version, fields: [...selected] });
      await utils.clients.invalidate();
      toast.success('Zapisano dane z rejestru');
      onOpenChange(false);
    } catch {
      // komunikat pod tabelą (apply.error)
    }
  }

  const rows = result?.company
    ? COMPANY_REGISTRY_FIELDS.map((field) => {
        const now = fieldText(field, client.company);
        const next = registryText(field, result.company);
        return { field, now, next, same: now === next || !next };
      })
    : [];
  const anyDifferent = rows.some((r) => !r.same);
  const applyError = apply.error
    ? appErrorCode(apply.error) === 'CONFLICT'
      ? 'Ktoś w międzyczasie zmienił dane klienta. Zamknij okno i otwórz je ponownie.'
      : errorMessage(apply.error)
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !apply.isPending && onOpenChange(next)}
      title="Odśwież dane z rejestru"
      description={nip ? `NIP ${formatNip(nip)}` : undefined}
      width="l"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={apply.isPending}>
            {result?.found && anyDifferent ? 'Anuluj' : 'Zamknij'}
          </Button>
          {result?.found && anyDifferent && (
            <Button
              variant="primary"
              onClick={() => void save()}
              loading={apply.isPending}
              disabled={selected.size === 0}
            >
              Zapisz wybrane
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!nip && (
          <p className="text-body-s text-fg-secondary">
            Klient nie ma polskiego NIP. Wpisz NIP w danych klienta, a potem odśwież dane.
          </p>
        )}
        {lookup.isPending && (
          <p className="inline-flex items-center gap-2 text-body-s text-fg-secondary" role="status">
            <Spinner size={14} /> Szukamy firmy w rejestrze…
          </p>
        )}
        {lookup.isError && (
          <p role="status" className="rounded-md bg-surface px-3 py-2 text-body-s text-fg-secondary">
            {errorMessage(lookup.error, 'Rejestr firm nie odpowiada.')} Dane klienta zostają bez zmian.
          </p>
        )}
        {result && !result.found && (
          <p role="status" className="rounded-md bg-surface px-3 py-2 text-body-s text-fg-secondary">
            Nie znaleźliśmy firmy z tym NIP w rejestrze. Dane klienta zostają bez zmian.
          </p>
        )}
        {result?.found && (
          <>
            {!anyDifferent && (
              <p role="status" className="text-body-s text-fg-secondary">
                Dane klienta zgadzają się z rejestrem.
              </p>
            )}
            <table className="w-full table-fixed text-body-s">
              <caption className="sr-only">Porównanie danych klienta z rejestrem</caption>
              <thead>
                <tr className="border-b border-line text-left text-caption text-fg-muted">
                  <th scope="col" className="w-[132px] py-2 font-medium">
                    Pole
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    Teraz
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    W rejestrze
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.field} className="border-b border-line align-top">
                    <th scope="row" className="py-2 pr-2 text-left font-normal">
                      {r.same ? (
                        <span className="text-fg-secondary">{COMPANY_REGISTRY_FIELD_LABELS[r.field]}</span>
                      ) : (
                        <Checkbox
                          checked={selected.has(r.field)}
                          onCheckedChange={(checked) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(r.field);
                              else next.delete(r.field);
                              return next;
                            })
                          }
                          label={COMPANY_REGISTRY_FIELD_LABELS[r.field]}
                        />
                      )}
                    </th>
                    <td className="py-2 pr-2 break-words text-fg-secondary">{r.now || '—'}</td>
                    <td className="py-2 break-words text-fg">
                      {r.next || '—'}
                      {r.same && r.next && <span className="block text-caption text-fg-muted">bez zmian</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-caption text-fg-muted">
              {registrySourceLabel(result.source, result.fetchedAt)}. Nazwa klienta na listach się nie zmienia.
            </p>
          </>
        )}
        {applyError && <FormMessage>{applyError}</FormMessage>}
      </div>
    </Dialog>
  );
}
